import { type ReactNode, type Ref, useEffect, useRef, useState } from 'react'
import { inLanguage } from '../../language/text.js'
import { type SingleProps, type ValuesProps, asText } from './kinds.js'
import { Combobox, needsSearch } from './SearchableChoice.js'

/**
 * Kiezen uit een lijst: `dropdown`, `radio`, `checkbox` en `multiselect`.
 *
 * Vier controltypes, twee vormen. `dropdown` en `radio` leveren één waarde,
 * `checkbox` en `multiselect` een verzameling (R-15, `RESPONSE_SHAPE` in het model).
 * Elke optie draagt zijn label per taal (R-143), dus de weergave wisselt mee met de
 * taalkeuze zonder dat de waarde verandert (R-144): opgeslagen wordt `waarde` van de
 * optie en nooit het label.
 *
 * **Een `checkbox` zonder opties is één aanvinkregel en toch een lijst.** Het model
 * laat `opties` daar weg (losse instemming), en de antwoordvorm blijft `waarden`.
 * De control maakt er dan één optie van met het label van het veld zelf; aangevinkt
 * staat er een lijst met één waarde in de stand. De echte enkele ja-nee-vraag is
 * `toggle`, en het akkoordveld is een variant daarvan (R-19, R-89).
 *
 * **Met meer dan tien opties zoekt de invuller** in plaats van te scrollen: `dropdown` en
 * `multiselect` worden dan een combobox die filtert op label en code (`SearchableChoice.tsx`,
 * R-166, G047). De waarde blijft dezelfde; alleen de manier van kiezen verandert.
 */

interface Option {
  readonly waarde: string
  readonly label: { readonly nl: string; readonly en: string }
}

function optionsOf(props: SingleProps | ValuesProps): readonly Option[] {
  const field = props.veld
  if ('opties' in field && field.opties !== undefined) return field.opties
  // Een checkbox zonder opties: het veld is zijn eigen enige optie.
  if (field.control === 'checkbox') {
    const label = field.label ?? { nl: 'Ja', en: 'Yes' }
    return [{ waarde: 'ja', label }]
  }
  return []
}

export function Dropdown(props: SingleProps): ReactNode {
  const { veld: field, taal: language, koppeling: link, waarde: value, onWaarde: onValue, bewerkbaar: editable } = props
  const options = optionsOf(props)
  const chosen = options.find((option) => option.waarde === value)
  const restText = chosen === undefined ? asText(value) : inLanguage(chosen.label, language)
  if (!editable) {
    // Een antwoord om te lezen en niet een vak om in te kiezen (R-86), bij elke lengte van de lijst en
    // niet alleen bij de zoekende: `readonly` en niet `disabled`, zodat het bereikbaar blijft zoals een
    // tekstveld dat alleen te lezen is. Een uitgeschakelde select las als een kapot veld.
    return <input className="ef-invoer" type="text" {...link} readOnly value={restText} />
  }
  if (needsSearch(options)) {
    return (
      <Combobox
        koppeling={link}
        taal={language}
        naam={field.label === undefined ? field.id : inLanguage(field.label, language)}
        opties={options}
        meerdere={false}
        gekozen={value === undefined ? [] : [String(value)]}
        rusttekst={restText}
        onKies={(chosenValue) => onValue(chosenValue)}
        onWis={() => onValue(undefined)}
      />
    )
  }
  return (
    <select
      className="ef-invoer"
      {...link}
      value={asText(value)}
      disabled={!editable}
      onChange={(event) => {
        const chosen = event.target.value
        onValue(chosen === '' ? undefined : chosen)
      }}
    >
      {/*
        Een leeg eerste item, want een dropdown zonder keuze mag niet stil de
        eerste optie aanwijzen: leeg is leeg, en anders staat er een antwoord dat
        niemand heeft gegeven (ontwerp 6.2).
      */}
      <option value="">{language === 'nl' ? 'Kies een optie' : 'Choose an option'}</option>
      {options.map((optie) => (
        <option key={optie.waarde} value={optie.waarde}>
          {inLanguage(optie.label, language)}
        </option>
      ))}
    </select>
  )
}

/**
 * Eén keuze uit weinig opties.
 *
 * Een `fieldset` met een `legend` en geen losse radio's: de vraag hoort bij de
 * groep en niet bij elke knop apart, en dat is wat een schermlezer aankondigt
 * (R-139). Het label van de veldschil staat daarom in de legend en niet nog eens
 * per optie.
 */
export function Radio(props: SingleProps): ReactNode {
  const { taal: language, koppeling: link, waarde: value, onWaarde: onValue, bewerkbaar: editable } = props
  const options = optionsOf(props)
  if (!editable) {
    // Alleen de gekozen optie, als tekst, en niet drie uitgeschakelde rondjes (R-86).
    const chosen = options.find((option) => option.waarde === value)
    return <input className="ef-invoer" type="text" {...link} readOnly value={chosen === undefined ? asText(value) : inLanguage(chosen.label, language)} />
  }
  return (
    <fieldset
      className="ef-keuzegroep"
      id={link.id}
      // `radiogroup` en niet de impliciete `group` van een fieldset: `aria-required`
      // mag op een radiogroup staan en op een group niet, en axe rekent dat aan
      // (aria-allowed-attr). De rol zegt hier ook precies wat het is: één keuze uit
      // deze knoppen.
      role="radiogroup"
      // De vraag hoort bij de groep en niet bij één van de knoppen: de kop van de
      // veldschil is de naam van de groep (R-139). Zou de eerste knop het id van de
      // schil dragen, dan werd juist die als "vraag plus optie" voorgelezen en de
      // andere alleen als optie.
      aria-labelledby={link['aria-labelledby']}
      aria-describedby={link['aria-describedby']}
      aria-invalid={link['aria-invalid']}
      aria-required={link['aria-required']}
    >
      {options.map((option) => (
        <label className="ef-keuze" key={option.waarde}>
          <input
            type="radio"
            name={link.id}
            value={option.waarde}
            checked={value === option.waarde}
            disabled={!editable}
            onChange={() => onValue(option.waarde)}
          />
          <span>{inLanguage(option.label, language)}</span>
        </label>
      ))}
    </fieldset>
  )
}

/** Een aanvinklijst: nul of meer opties (R-15). */
export function Checklist(props: ValuesProps): ReactNode {
  const { taal: language, koppeling: link, waarden: values, onValues, bewerkbaar: editable } = props
  const options = optionsOf(props)
  const transition = (value: string): void => {
    onValues(values.includes(value) ? values.filter((w) => w !== value) : [...values, value])
  }
  if (!editable) {
    // De gekozen opties als lijst, zonder vinkjes (R-86): wie leest, ziet het antwoord en niet het formulier.
    return (
      <ul className="ef-keuzelezen" id={link.id} aria-labelledby={link['aria-labelledby']} aria-describedby={link['aria-describedby']}>
        {values.map((value) => {
          const option = options.find((candidate) => candidate.waarde === value)
          return <li key={String(value)}>{option === undefined ? String(value) : inLanguage(option.label, language)}</li>
        })}
      </ul>
    )
  }
  return (
    <fieldset
      className="ef-keuzegroep"
      id={link.id}
      aria-labelledby={link['aria-labelledby']}
      aria-describedby={link['aria-describedby']}
      aria-invalid={link['aria-invalid']}
      // Geen `aria-required` op een aanvinklijst: dat attribuut mag niet op een
      // `group` en er is geen rol die "kies er minstens één" uitdrukt. De
      // verplichting staat in de aanduiding bij de kop en in de melding uit de regel
      // (R-09, R-30).
    >
      {options.map((option) => (
        <label className="ef-keuze" key={option.waarde}>
          <input
            type="checkbox"
            value={option.waarde}
            checked={values.includes(option.waarde)}
            disabled={!editable}
            onChange={() => transition(option.waarde)}
          />
          <span>{inLanguage(option.label, language)}</span>
        </label>
      ))}
    </fieldset>
  )
}

/**
 * Meerdere keuzes als verwijderbare chips (R-15).
 *
 * De chips zijn knoppen met een eigen toegankelijke naam ("verwijder X"), zodat een
 * schermlezergebruiker weet wat hij weghaalt; een kruisje alleen is voor hem stil.
 * Na het weghalen gaat de focus naar de volgende chip, of de vorige, of het invoervak,
 * en niet naar het begin van de pagina.
 *
 * Kiezen loopt via een `select`, en bij meer dan tien opties via de zoekende combobox
 * (`SearchableChoice.tsx`, R-166, G047): daar blijft de lijst open na een keuze, zodat
 * de invuller er meer kan kiezen.
 */
export function ChipsChoice(props: ValuesProps): ReactNode {
  const { taal: language, koppeling: link, waarden: values, onValues, bewerkbaar: editable } = props
  const options = optionsOf(props)
  /**
   * Een `tag` heeft geen opties in het model (`zonderInstellingen('tag')`): het zijn
   * trefwoorden die de invuller zelf bedenkt, geen keuze uit een lijst (ontwerp 6.2).
   * Zonder deze tak zou zo'n veld een lege keuzelijst zijn en dus onvulbaar, terwijl
   * `minimumAantal` erop kan staan.
   */
  const freeEntry = options.length === 0
  const searchable = !freeEntry && needsSearch(options)
  const available = options.filter((option) => !values.includes(option.waarde))
  const labelOf = (value: string | number): string => {
    const option = options.find((o) => o.waarde === value)
    return option === undefined ? String(value) : inLanguage(option.label, language)
  }

  /**
   * Waar de focus heen gaat na het weghalen van een chip: de plek van de weggehaalde in de nieuwe
   * lijst. Na de hertekening, want dan bestaan de knoppen van de nieuwe lijst pas.
   */
  const list = useRef<HTMLUListElement | null>(null)
  const entry = useRef<HTMLInputElement | HTMLSelectElement | null>(null)
  const pendingFocus = useRef<number | undefined>(undefined)
  useEffect(() => {
    const at = pendingFocus.current
    if (at === undefined) return
    pendingFocus.current = undefined
    const buttons = [...(list.current?.querySelectorAll<HTMLButtonElement>('.ef-chip__weg') ?? [])]
    ;(buttons[at] ?? buttons[at - 1] ?? entry.current)?.focus()
  }, [values])
  const remove = (index: number, value: string | number): void => {
    pendingFocus.current = index
    onValues(values.filter((w) => w !== value))
  }

  return (
    <div className="ef-chips">
      <ul className="ef-chips__lijst" ref={list}>
        {values.map((value, index) => (
          <li className="ef-chip" key={String(value)}>
            <span>{labelOf(value)}</span>
            {editable ? (
              <button
                type="button"
                className="ef-chip__weg"
                aria-label={
                  language === 'nl' ? `Verwijder ${labelOf(value)}` : `Remove ${labelOf(value)}`
                }
                onClick={() => remove(index, value)}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {freeEntry ? (
        <KeywordInput
          invoerRef={(element) => {
            entry.current = element
          }}
          koppeling={link}
          taal={language}
          bewerkbaar={editable}
          bestaand={values}
          onAdd={(word) => onValues([...values, word])}
        />
      ) : searchable ? (
        // Wie alleen leest, ziet de chips en geen vak om in te zoeken (R-86).
        editable ? (
          <Combobox
            invoerRef={(element) => {
              entry.current = element
            }}
            koppeling={link}
            taal={language}
            naam={props.veld.label === undefined ? props.veld.id : inLanguage(props.veld.label, language)}
            opties={options}
            meerdere
            gekozen={values}
            rusttekst=""
            onKies={(chosen) => onValues(values.includes(chosen) ? values.filter((w) => w !== chosen) : [...values, chosen])}
          />
        ) : null
      ) : !editable ? null : (
        // Wie alleen leest, ziet de chips en geen keuzelijst om iets toe te voegen (R-86).
        <select
          ref={(element) => {
            entry.current = element
          }}
          className="ef-invoer"
          {...link}
          value=""
          disabled={available.length === 0}
          onChange={(event) => {
            const chosen = event.target.value
            if (chosen !== '') onValues([...values, chosen])
          }}
        >
          <option value="">{language === 'nl' ? 'Voeg toe' : 'Add'}</option>
          {available.map((optie) => (
            <option key={optie.waarde} value={optie.waarde}>
              {inLanguage(optie.label, language)}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

/**
 * Vrije trefwoorden: typen en toevoegen, elk trefwoord wordt een chip (R-15).
 *
 * Enter voegt toe en de knop doet hetzelfde, want een invoerveld waar alleen Enter
 * werkt is met een schermlezer niet te vinden. Een leeg of dubbel trefwoord doet
 * niets: het model weigert dezelfde waarde twee keer.
 */
function KeywordInput({
  invoerRef,
  koppeling: link,
  taal: language,
  bewerkbaar: editable,
  bestaand: existing,
  onAdd,
}: {
  invoerRef?: Ref<HTMLInputElement>
  koppeling: ValuesProps['koppeling']
  taal: ValuesProps['taal']
  bewerkbaar: boolean
  bestaand: readonly (string | number)[]
  onAdd: (word: string) => void
}): ReactNode {
  const [text, setText] = useState('')
  const add = (): void => {
    const word = text.trim()
    if (word === '' || existing.includes(word)) return
    onAdd(word)
    setText('')
  }
  // Wie niet kan schrijven ziet de trefwoorden en geen leeg invoervak met een knop die niets doet
  // (audit van 30 oktober 2026, R-86).
  if (!editable) return null
  return (
    <span className="ef-trefwoorden">
      <input
        ref={invoerRef}
        className="ef-invoer"
        type="text"
        {...link}
        value={text}
        readOnly={!editable}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          // Anders verstuurt Enter het omliggende formulier.
          event.preventDefault()
          add()
        }}
      />
      <button type="button" className="ef-trefwoorden__erbij" disabled={!editable} onClick={add}>
        {language === 'nl' ? 'Toevoegen' : 'Add'}
      </button>
    </span>
  )
}
